import React, { useState, useEffect, FormEvent } from 'react';

import { useSelector } from 'react-redux';
import { selectUser } from '../features/userSlice';

import firebase from 'firebase/app';
import { db, storage, timestamp } from '../firebase';
import { getFriendUIDListFn } from '../firebase';

import {
  Avatar,
  Button,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  FormControlLabel,
  Switch,
  Divider,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddAPhotoIcon from '@material-ui/icons/AddAPhoto';
import SendIcon from '@material-ui/icons/Send';

import GenericTemplate from '../components/templates/GenericTemplate';
import styles from './Styles.module.css';

// xross interfaces
import {
  XROSS_POST_EX,
  CONTENTS_ATTRIBUTE,
  PostStatus,
  // PostCategory,
} from '../common/interface';
import { PostCategory } from '$common/define';

// lib
import { textAnalyzer } from '../common/lib/textAnalyzer';

// firebase lib
import { createPost } from '../common/firebase/post';
import { getXrossUserProfile } from '../common/firebase/user';

// マップピッカー
import AdminPostMapPicker from '../components/AdminPostMapPicker';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  sectionCard: {
    marginBottom: theme.spacing(2),
  },
  imagePreview: {
    maxWidth: '100%',
    maxHeight: 240,
    objectFit: 'contain',
    borderRadius: 8,
    border: '1px solid #eee',
  },
  fileInput: {
    display: 'none',
  },
  actionsRight: {
    marginLeft: 'auto',
  },
  hashtagField: {
    marginTop: theme.spacing(1),
  },
}));

// ------------------------------------------------------------------
// CreatePost（運営者用）
// ------------------------------------------------------------------

const CreatePost: React.FC = () => {
  const classes = useStyles();

  // redux ログインユーザ
  const user = useSelector(selectUser);
  const avatar = user.photoUrl;
  const uid = user.uid;

  // フォーム state
  // const [imageFile, setImageFile] = useState<File | null>(null);
  // const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  // 変更後（★複数対応）
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);

  const [text, setText] = useState('');
  const [hashtagText, setHashtagText] = useState(''); // 手動ハッシュタグ編集用
  const [displayLocationLabel, setDisplayLocationLabel] = useState('');
  const [location, setLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [disclosureRange, setDisclosureRange] =
    useState<string>('_BETWEEN_FRIENDS_');
  const [category, setCategory] = useState<PostCategory>('_NORMAL_');
  const [postStatus, setPostStatus] = useState<PostStatus>('_STORED_');
  const [isPr, setIsPr] = useState<boolean>(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [profile, setProfile] = useState<any>(null);
  const [deliveryUidList, setDeliveryUidList] = useState<string[]>([]);

  // 自動ハッシュタグ抽出 → 手動フィールドにもデフォルト表示
  useEffect(() => {
    const result = textAnalyzer(text);
    const tags = result.tags || [];
    setHashtagText(tags.join(' '));
  }, [text]);

  /*
   * -----------------------------------------------------------------
   * 投稿者のプロフィール情報を取得する
   * -----------------------------------------------------------------
   */
  useEffect(() => {
    if (!uid) return;
    const fetch = async () => {
      const profile = await getXrossUserProfile(uid);
      if (profile) {
        setProfile(profile);
      }
    };

    fetch();
  }, [uid]);

  useEffect(() => {
    if (!profile) return;

    // 位置ラベルが未入力 → プロフィールからセット
    if (profile.homeGLocationLabel) {
      setDisplayLocationLabel(
        (prev) => prev || profile.homeGLocationLabel || ''
      );
    }

    // 位置が未指定 → プロフィールの homeGLocation を反映
    if (profile.homeGLocation) {
      const { latitude, longitude } = profile.homeGLocation;
      if (
        typeof latitude === 'number' &&
        typeof longitude === 'number' &&
        !(latitude === 0 && longitude === 0)
      ) {
        setLocation((prev) => {
          // すでにユーザがマップで選んでいたら上書きしない
          if (prev) return prev;
          return { latitude, longitude };
        });
      }
    }
  }, [profile]);

  /*
   * -----------------------------------------------------------------
   * 友達リスト（UID）を取得する（自分のUIDを含む）
   * -----------------------------------------------------------------
   */
  useEffect(() => {
    if (!uid) return;
    const fetchFriends = async () => {
      try {
        const result = await getFriendUIDListFn();
        const data = result.data;
        const list = Array.from(new Set([data.uid, ...(data.uidList || [])]));
        setDeliveryUidList(list);
      } catch (err) {
        console.error(err);
        setDeliveryUidList([uid]);
      }
    };

    fetchFriends();
  }, [uid]);

  // ボタン活性条件
  // const canSubmit =
  //   !!uid &&
  //   !!text.trim() &&
  //   !!imageFile &&
  //   !!location &&
  //   !!displayLocationLabel.trim() &&
  //   !isSubmitting;
  // 変更後（★最低1枚あればOK）
  const canSubmit =
    !!uid &&
    !!text.trim() &&
    imageFiles.length > 0 &&
    !!location &&
    !!displayLocationLabel.trim() &&
    !isSubmitting;

  /*
   * -----------------------------------------------------------------
   * 画像ピックアップ
   * -----------------------------------------------------------------
   */
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const newFiles = Array.from(e.target.files);
    // 既存のファイルに追加していく場合は concat に
    setImageFiles((prev) => [...prev, ...newFiles]);

    const newPreviews = newFiles.map((file) => URL.createObjectURL(file));
    setImagePreviewUrls((prev) => [...prev, ...newPreviews]);

    // 同じファイルを連続選択できるようにクリア
    e.target.value = '';
  };

  // const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (!e.target.files || !e.target.files[0]) return;
  //   const file = e.target.files[0];
  //   setImageFile(file);
  //   setImagePreviewUrl(URL.createObjectURL(file));
  //   e.target.value = '';
  // };

  /*
   * -----------------------------------------------------------------
   * 投稿データ作成
   * -----------------------------------------------------------------
   */
  const buildPostData = (imageUrls: string[]): XROSS_POST_EX => {
    const gLocation = location || {
      latitude: 0,
      longitude: 0,
    };

    // ★ contents は全メディア URL
    const coverContents: string[] = imageUrls;

    // ★ contentsAttributeList も複数分作成
    const contentsAttributeList: CONTENTS_ATTRIBUTE[] = imageUrls.map(
      (url) => ({
        coverContents: url, // サムネイルとして同じURLを指定
        mediaType: 'image',
        mediaUrl: url,
      })
    );

    const manualTags = hashtagText
      .split(/[ ,　]+/)
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    return {
      id: '',
      uid,
      userName: user.displayName || '',
      avatarUrl: avatar,
      text,
      hashtag: manualTags,
      friendtag: [],
      contents: coverContents,
      contentsAttributeList,
      gLocation,
      displayGLocation: gLocation,
      displayGLocationLabel: displayLocationLabel,
      disclosureRange,
      deliveryUIDList: deliveryUidList.length > 0 ? deliveryUidList : [uid],
      postStatus,
      category,
      createdAt: timestamp as firebase.firestore.Timestamp,
      lastRefreshAt: timestamp as firebase.firestore.Timestamp,
      pr: isPr,
    };
  };

  // const buildPostData = (imageUrl: string): XROSS_POST_EX => {
  //   const gLocation = location || {
  //     latitude: 0,
  //     longitude: 0,
  //   };

  //   const coverContents: string[] = [imageUrl];
  //   const contentsAttributeList: CONTENTS_ATTRIBUTE[] = [
  //     {
  //       coverContents: imageUrl,
  //       mediaType: 'image',
  //       mediaUrl: imageUrl,
  //     },
  //   ];

  //   const manualTags = hashtagText
  //     .split(/[ ,　]+/)
  //     .map((t) => t.trim())
  //     .filter((t) => t.length > 0);

  //   return {
  //     id: '',
  //     uid,
  //     userName: user.displayName || '', // 必要ならここは手入力項目にしてもOK
  //     avatarUrl: avatar,
  //     text,
  //     hashtag: manualTags,
  //     friendtag: [], // 将来的にタグ付けUIをつけるならここを更新
  //     contents: coverContents,
  //     contentsAttributeList,
  //     gLocation,
  //     displayGLocation: gLocation,
  //     displayGLocationLabel: displayLocationLabel,
  //     disclosureRange,
  //     // deliveryUIDList: [uid], // ひとまず投稿者自身のみ / 将来拡張用
  //     deliveryUIDList: deliveryUidList.length > 0 ? deliveryUidList : [uid],
  //     postStatus,
  //     category,
  //     createdAt: timestamp as firebase.firestore.Timestamp,
  //     lastRefreshAt: timestamp as firebase.firestore.Timestamp,
  //     pr: isPr,
  //   };
  // };

  // 投稿処理
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (imageFiles.length === 0 || !location) return;
    if (!canSubmit) return;

    try {
      setIsSubmitting(true);

      // ★1ファイルをアップロードして URL を返すヘルパー
      const uploadSingleFile = (file: File): Promise<string> => {
        return new Promise<string>((resolve, reject) => {
          const unique = Math.random().toString(36).slice(-8);
          const name = file.name;
          const path = `contents/admin/${uid}/${unique}_${name}`;

          const uploadTask = storage.ref(path).put(file);

          uploadTask.on(
            firebase.storage.TaskEvent.STATE_CHANGED,
            () => {},
            (err) => reject(err),
            async () => {
              try {
                const url = await storage.ref(path).getDownloadURL();
                resolve(url);
              } catch (err) {
                reject(err);
              }
            }
          );
        });
      };

      // ★全画像をアップロード
      const imageUrls = await Promise.all(
        imageFiles.map((file) => uploadSingleFile(file))
      );

      console.log('Image URLs:', imageUrls);

      const postData = buildPostData(imageUrls);

      console.log('postData:', postData);
      await createPost(postData);

      // 完了後リセット
      setImageFiles([]);
      setImagePreviewUrls([]);
      setText('');
      setHashtagText('');
      setLocation(null);
      setDisplayLocationLabel('');
      setDisclosureRange('_BETWEEN_FRIENDS_');
      setCategory('_NORMAL_');
      setPostStatus('_STORED_');
      setIsPr(false);
      alert('投稿を作成しました。');
    } catch (err) {
      console.error(err);
      alert('投稿の作成に失敗しました。');
    } finally {
      setIsSubmitting(false);
    }
  };

  // const handleSubmit = async (e: FormEvent) => {
  //   e.preventDefault();
  //   if (!imageFile || !location) return;
  //   if (!canSubmit) return;

  //   try {
  //     setIsSubmitting(true);

  //     const unique = Math.random().toString(36).slice(-8);
  //     const name = imageFile.name;
  //     const path = `contents/admin/${uid}/${unique}_${name}`;

  //     const uploadTask = storage.ref(path).put(imageFile);

  //     // アップロード完了を待つ
  //     await new Promise<void>((resolve, reject) => {
  //       uploadTask.on(
  //         firebase.storage.TaskEvent.STATE_CHANGED,
  //         () => {},
  //         (err) => reject(err),
  //         () => resolve()
  //       );
  //     });

  //     console.log('Image uploaded to:', path);
  //     const url = await storage.ref(path).getDownloadURL();

  //     const postData = buildPostData(url);

  //     console.log('postData:', postData);
  //     await createPost(postData);

  //     // 完了後リセット
  //     setImageFile(null);
  //     setImagePreviewUrl(null);
  //     setText('');
  //     setHashtagText('');
  //     setLocation(null);
  //     setDisplayLocationLabel('');
  //     setDisclosureRange('_BETWEEN_FRIENDS_');
  //     setCategory('_NORMAL_');
  //     setPostStatus('_STORED_');
  //     setIsPr(false);
  //     alert('投稿を作成しました。');
  //   } catch (err) {
  //     console.error(err);
  //     alert('投稿の作成に失敗しました。');
  //   } finally {
  //     setIsSubmitting(false);
  //   }
  // };

  return (
    <GenericTemplate title="新規投稿（運営用）">
      <form onSubmit={handleSubmit} className={classes.root}>
        <Grid container spacing={2}>
          {/* 左カラム：入力フォーム */}
          <Grid item xs={12} md={7}>
            {/* 基本情報 */}
            <Card className={classes.sectionCard}>
              <CardHeader
                avatar={<Avatar src={avatar} />}
                title="基本情報"
                subheader={`投稿者: ${user.displayName || uid}`}
              />
              <CardContent>
                <TextField
                  label="投稿コメント"
                  multiline
                  fullWidth
                  rows={4}
                  variant="outlined"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  required
                  helperText="投稿本文を入力してください（必須）"
                />

                <TextField
                  className={classes.hashtagField}
                  label="ハッシュタグ（空白区切り）"
                  fullWidth
                  variant="outlined"
                  value={hashtagText}
                  onChange={(e) => setHashtagText(e.target.value)}
                  helperText="自動抽出されたタグを編集できます"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={isPr}
                      onChange={(e) => setIsPr(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="公式投稿として表示（投稿範囲は変わらない）"
                />
              </CardContent>
            </Card>

            {/* メディア */}
            <Card className={classes.sectionCard}>
              <CardHeader title="メディア" />
              <CardContent>
                <input
                  id="post-image-input"
                  type="file"
                  accept="image/*"
                  multiple // ★ここ追加：複数選択
                  className={classes.fileInput}
                  onChange={handleImageChange}
                />
                <label htmlFor="post-image-input">
                  <Button
                    variant="outlined"
                    startIcon={<AddAPhotoIcon />}
                    component="span"
                  >
                    画像を選択（複数可）
                  </Button>
                </label>

                {imagePreviewUrls.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    {imageFiles.map((file, index) => (
                      <div key={index} style={{ marginBottom: 8 }}>
                        <Typography variant="body2">{file.name}</Typography>
                        <img
                          src={imagePreviewUrls[index]}
                          alt={`preview-${index}`}
                          className={classes.imagePreview}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {imageFiles.length === 0 && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ marginTop: 8, display: 'block' }}
                  >
                    画像は必須です（運営投稿では最低1枚の画像を添付してください）
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* 公開設定 */}
            <Card className={classes.sectionCard}>
              <CardHeader title="公開設定" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel id="disclosure-range-label">
                        公開範囲
                      </InputLabel>
                      <Select
                        labelId="disclosure-range-label"
                        label="公開範囲"
                        value={disclosureRange}
                        onChange={(e) =>
                          setDisclosureRange(e.target.value as string)
                        }
                      >
                        <MenuItem value="_PUBLIC_">全体公開</MenuItem>
                        <MenuItem value="_BETWEEN_FRIENDS_">
                          フレンドまで
                        </MenuItem>
                        <MenuItem value="_PERSONAL_">自分のみ</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel id="category-label">カテゴリ</InputLabel>
                      <Select
                        labelId="category-label"
                        label="カテゴリ"
                        value={category}
                        onChange={(e) =>
                          setCategory(e.target.value as PostCategory)
                        }
                      >
                        <MenuItem value="_NORMAL_">通常投稿</MenuItem>
                        {/* <MenuItem value="_EVENT_">イベント</MenuItem>
                        <MenuItem value="_CAMPAIGN_">キャンペーン</MenuItem> */}
                        <MenuItem value="_PR_">広告（全員に表示）</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel id="post-status-label">
                        投稿ステータス
                      </InputLabel>
                      <Select
                        labelId="post-status-label"
                        label="投稿ステータス"
                        value={postStatus}
                        onChange={(e) =>
                          setPostStatus(e.target.value as PostStatus)
                        }
                      >
                        <MenuItem value="_STORED_">公開</MenuItem>
                        <MenuItem value="_TEMPORARILY_">下書き保存</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* 右カラム：位置情報 + プレビュー */}
          <Grid item xs={12} md={5}>
            {/* 位置情報 */}
            <Card className={classes.sectionCard}>
              <CardHeader title="位置情報" />
              <CardContent>
                <Typography variant="body2" gutterBottom>
                  マップ上をクリックして投稿位置を指定してください（必須）
                </Typography>
                <AdminPostMapPicker
                  value={location}
                  onChange={(loc) => setLocation(loc)}
                />

                <TextField
                  style={{ marginTop: 8 }}
                  label="位置ラベル（スポット名など）"
                  variant="outlined"
                  fullWidth
                  value={displayLocationLabel}
                  onChange={(e) => setDisplayLocationLabel(e.target.value)}
                  helperText="例）○○公園 展望台 / △△海岸 など"
                  required
                />

                {location && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ marginTop: 4, display: 'block' }}
                  >
                    緯度: {location.latitude.toFixed(6)} / 経度:{' '}
                    {location.longitude.toFixed(6)}
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* プレビュー */}
            <Card className={classes.sectionCard}>
              <CardHeader title="投稿プレビュー" />
              <CardContent>
                <div style={{ display: 'flex', marginBottom: 8 }}>
                  <Avatar src={avatar} />
                  <div style={{ marginLeft: 8 }}>
                    <Typography variant="subtitle2">
                      {user.displayName || '運営アカウント'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {displayLocationLabel || '位置情報未設定'}
                    </Typography>
                  </div>
                </div>

                {imagePreviewUrls.length > 0 && (
                  <div>
                    {imagePreviewUrls.map((url, index) => (
                      <img
                        key={index}
                        src={url}
                        alt={`preview-${index}`}
                        className={classes.imagePreview}
                        style={{ marginBottom: 8 }}
                      />
                    ))}
                  </div>
                )}
                <Typography
                  variant="body2"
                  style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}
                >
                  {text || '投稿本文がここに表示されます'}
                </Typography>
                {hashtagText && (
                  <Typography
                    variant="caption"
                    color="primary"
                    style={{ marginTop: 4, display: 'block' }}
                  >
                    {hashtagText
                      .split(/[ ,　]+/)
                      .filter((t) => t)
                      .map((t) => (t.startsWith('#') ? t : `#${t}`))
                      .join(' ')}
                  </Typography>
                )}
                <Divider style={{ margin: '8px 0' }} />
                <Typography variant="caption" color="textSecondary">
                  公開範囲:{' '}
                  {disclosureRange === '_PUBLIC_'
                    ? '全体公開'
                    : disclosureRange === '_BETWEEN_FRIENDS_'
                    ? 'フレンドまで'
                    : '自分のみ'}
                  {' ／ '}
                  カテゴリ: {category}
                  {isPr && ' ／ PR投稿'}
                </Typography>
              </CardContent>
              <CardActions>
                <div className={classes.actionsRight}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!canSubmit}
                    startIcon={<SendIcon />}
                  >
                    {postStatus === '_STORED_' ? '公開する' : '下書き保存'}
                  </Button>
                </div>
              </CardActions>
            </Card>
          </Grid>
        </Grid>
      </form>
    </GenericTemplate>
  );
};

export default CreatePost;
